/**
 * BUNKER 84 — instance.
 *
 * v0.1.1 (sc-4534): manifest + black-screen title placeholder.
 * v0.1.2 (sc-4535): map data + collision wiring.
 * v0.1.3 (sc-4536): DDA raycaster + flat-shaded column renderer.
 * v0.1.4 (sc-4537): turn / strafe / forward input mapping.
 * v0.1.5 (sc-4538): hitscan weapon + first enemy type + HP + damage
 *   + score + game-over / mission-complete flow. The closing loop —
 *   the cartridge is now a complete (if minimal) arcade experience.
 *
 *   PERF GATE (sc-4536): press 'C' to toggle the perf HUD. Raycast +
 *   blit ms + budget readout in the top-right.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  castHitscan,
  DEATH_LINGER_FRAMES,
  GUARD_AGGRO_DELAY,
  GUARD_FIRE_COOLDOWN,
  GUARD_SIGHT_TILES,
  hasLineOfSight,
  makeLevel1Enemies,
  type Enemy,
} from './enemies.js';
import { LEVEL1_MAP, LEVEL1_SPAWN } from './level1.js';
import {
  CARD_BG,
  CARD_BORDER,
  HUD_HP_FULL,
  HUD_HP_LOW,
  HUD_SCORE,
  HUD_TEXT,
  TITLE_TEXT,
} from './palette.js';
import { moveWithCollision, type Player } from './player.js';
import { Renderer } from './renderer.js';

const WIDTH = 480;
const HEIGHT = 270;

/** Movement speeds in tile-units / second (60Hz fixed timestep). */
const MOVE_SPEED = 3.0;
const TURN_SPEED = 2.5;

/** Rolling window of frame timings for the perf overlay average. */
const PERF_WINDOW = 30;

/** Player constants. */
const PLAYER_MAX_HP = 3;
const FIRE_COOLDOWN = 15; // ≤ 4 shots/sec
const KILL_SCORE = 100;
const COMPLETION_BONUS = 500;

type Phase = 'playing' | 'game-over' | 'mission-complete';

export class Bunker84Instance implements GameInstance {
  private readonly ctx: GameContext;
  private readonly map: Uint8Array;
  private readonly player: Player;
  private readonly renderer = new Renderer();

  private enemies: Enemy[];
  private phase: Phase = 'playing';
  private tick = 0;
  private phaseEnteredTick = 0;
  private hp = PLAYER_MAX_HP;
  private score = 0;
  private lastFireTick = -FIRE_COOLDOWN;
  /** Tick of last damage taken — used to flash the screen red. */
  private lastHurtTick = -1000;
  /** Tick of last shot fired — used to render a brief muzzle flash. */
  private muzzleFlashUntil = 0;

  // Perf HUD.
  private perfHudOn = false;
  private readonly raycastSamples = new Float32Array(PERF_WINDOW);
  private readonly blitSamples = new Float32Array(PERF_WINDOW);
  private perfIdx = 0;

  // Per-frame input.
  private inForward = 0;
  private inStrafe = 0;
  private inTurn = 0;
  private inFireEdge = false;
  private inRestartEdge = false;
  private togglePerfNext = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    void this.ctx.random.next();

    this.map = LEVEL1_MAP;
    this.player = {
      x: LEVEL1_SPAWN.x,
      y: LEVEL1_SPAWN.y,
      angle: LEVEL1_SPAWN.angle,
    };
    this.enemies = makeLevel1Enemies();
  }

  input(state: InputState): void {
    // Forward / back.
    let fwd = 0;
    if (state.up) fwd += 1;
    if (state.down) fwd -= 1;
    if (Math.abs(state.stickL.y) > 0.15) fwd -= state.stickL.y;
    this.inForward = clamp(fwd, -1, 1);

    // Left/right: turn by default, strafe while fireB (X) is held.
    const lr = (state.right ? 1 : 0) - (state.left ? 1 : 0);
    if (state.fireB && lr !== 0) {
      this.inStrafe = lr;
      this.inTurn = 0;
    } else {
      this.inTurn = lr;
      this.inStrafe = 0;
    }
    if (Math.abs(state.stickL.x) > 0.15) {
      this.inStrafe = clamp(this.inStrafe + state.stickL.x, -1, 1);
    }

    // FIRE — Z (fireA) OR Space (start). v0.1 = single-shot only
    // (edge), so we don't read the held state. End-card restart uses
    // the same fire button so the muscle memory carries over.
    this.inFireEdge =
      state.justPressed.has('fireA') || state.justPressed.has('start');
    this.inRestartEdge = this.inFireEdge;

    if (state.justPressed.has('fireC')) {
      this.togglePerfNext = true;
    }
  }

  update(dt: number): void {
    this.tick++;

    if (this.togglePerfNext) {
      this.perfHudOn = !this.perfHudOn;
      this.togglePerfNext = false;
    }

    if (this.phase !== 'playing') {
      this.tickEndCard();
      return;
    }

    this.tickPlayer(dt);
    this.tickEnemies();
    this.tickFire();
    this.tickCleanupEnemies();
    this.checkPhaseTransitions();
  }

  private tickPlayer(dt: number): void {
    if (this.inTurn !== 0) {
      this.player.angle += this.inTurn * TURN_SPEED * dt;
    }
    if (this.inForward !== 0 || this.inStrafe !== 0) {
      const dirX = Math.cos(this.player.angle);
      const dirY = Math.sin(this.player.angle);
      const dx = (dirX * this.inForward - dirY * this.inStrafe) * MOVE_SPEED * dt;
      const dy = (dirY * this.inForward + dirX * this.inStrafe) * MOVE_SPEED * dt;
      moveWithCollision(this.player, dx, dy, this.map);
    }
  }

  private tickEnemies(): void {
    // v0.1 AI: stand still. On FIRST LOS to the player, record an
    // `acquiredTick` and wait GUARD_AGGRO_DELAY frames before opening
    // fire — that's the "alert" beat that gives the player time to
    // react. After that, fire every GUARD_FIRE_COOLDOWN frames while
    // LOS holds. Losing LOS resets the acquired state so the next
    // sighting also gives the heads-up beat.
    // Patrol + multi-state AI proper lands in sc-4543.
    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i]!;
      if (e.state !== 'alive') continue;

      const dx = this.player.x - e.x;
      const dy = this.player.y - e.y;
      const dist2 = dx * dx + dy * dy;
      const inSight = dist2 <= GUARD_SIGHT_TILES * GUARD_SIGHT_TILES
        && hasLineOfSight(e.x, e.y, this.player.x, this.player.y, this.map);

      if (!inSight) {
        e.acquiredTick = -1; // lost the player
        continue;
      }

      if (e.acquiredTick < 0) {
        e.acquiredTick = this.tick;
        continue; // first frame of sighting — no shot yet
      }

      // Wait the aggro delay before the first shot.
      if (this.tick - e.acquiredTick < GUARD_AGGRO_DELAY) continue;

      if (this.tick - e.lastShotTick >= GUARD_FIRE_COOLDOWN) {
        e.lastShotTick = this.tick;
        this.applyDamage(1);
      }
    }
  }

  private tickFire(): void {
    if (!this.inFireEdge) return;
    if (this.tick - this.lastFireTick < FIRE_COOLDOWN) return;
    this.lastFireTick = this.tick;
    this.muzzleFlashUntil = this.tick + 4;

    const dirX = Math.cos(this.player.angle);
    const dirY = Math.sin(this.player.angle);
    const hit = castHitscan(this.player, dirX, dirY, this.enemies, this.map);
    if (hit.enemyIndex >= 0 && hit.enemyDist < hit.wallDist) {
      const e = this.enemies[hit.enemyIndex]!;
      e.state = 'dying';
      e.diedAtTick = this.tick;
      this.score += KILL_SCORE;
      this.ctx.events.emit('score', { score: this.score, delta: KILL_SCORE });
    }

    // Consume edge so a held button doesn't re-fire next frame.
    this.inFireEdge = false;
  }

  private tickCleanupEnemies(): void {
    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i]!;
      if (e.state === 'dying' && this.tick - e.diedAtTick >= DEATH_LINGER_FRAMES) {
        e.state = 'dead';
      }
    }
  }

  private applyDamage(amount: number): void {
    this.hp -= amount;
    this.lastHurtTick = this.tick;
    this.ctx.events.emit('death', { remainingLives: Math.max(0, this.hp) });
    if (this.hp <= 0) {
      this.hp = 0;
      this.enterPhase('game-over');
    }
  }

  private checkPhaseTransitions(): void {
    const remaining = this.enemies.reduce(
      (n, e) => n + (e.state === 'alive' ? 1 : 0),
      0
    );
    if (remaining === 0) {
      this.score += COMPLETION_BONUS;
      this.ctx.events.emit('score', { score: this.score, delta: COMPLETION_BONUS });
      this.enterPhase('mission-complete');
    }
  }

  private enterPhase(p: Phase): void {
    this.phase = p;
    this.phaseEnteredTick = this.tick;
  }

  private tickEndCard(): void {
    // Wait a short beat so the player sees the death/win moment before
    // their next button press can restart. Without this, an enemy that
    // killed you while you were already firing would immediately
    // restart the run.
    if (this.tick - this.phaseEnteredTick < 30) return;
    if (this.inRestartEdge) {
      this.restart();
    }
  }

  private restart(): void {
    this.player.x = LEVEL1_SPAWN.x;
    this.player.y = LEVEL1_SPAWN.y;
    this.player.angle = LEVEL1_SPAWN.angle;
    this.enemies = makeLevel1Enemies();
    this.hp = PLAYER_MAX_HP;
    this.score = 0;
    this.lastFireTick = -FIRE_COOLDOWN;
    this.lastHurtTick = -1000;
    this.muzzleFlashUntil = 0;
    this.phase = 'playing';
    this.phaseEnteredTick = this.tick;
    this.inRestartEdge = false;
  }

  render(g: CanvasRenderingContext2D): void {
    this.renderer.render(g, this.player, this.map, this.enemies);

    const timing = this.renderer.lastTiming;
    this.raycastSamples[this.perfIdx] = timing.raycastMs;
    this.blitSamples[this.perfIdx] = timing.blitMs;
    this.perfIdx = (this.perfIdx + 1) % PERF_WINDOW;

    this.drawMuzzleFlash(g);
    this.drawHurtFlash(g);
    this.drawHud(g);
    this.drawCrosshair(g);

    if (this.phase === 'game-over') {
      this.drawCard(g, 'YOU DIED', `SCORE ${this.score}`, 'PRESS FIRE TO RESTART');
    } else if (this.phase === 'mission-complete') {
      this.drawCard(
        g,
        'MISSION COMPLETE',
        `SCORE ${this.score}`,
        'PRESS FIRE TO RESTART'
      );
    }

    if (this.perfHudOn) this.drawPerfHud(g);
  }

  private drawMuzzleFlash(g: CanvasRenderingContext2D): void {
    if (this.tick >= this.muzzleFlashUntil) return;
    // A bright wedge at screen-bottom-center implying the weapon firing
    // out of the player's hand. Full-screen flash would be too much.
    g.fillStyle = 'rgba(255, 220, 120, 0.35)';
    g.fillRect(WIDTH / 2 - 60, HEIGHT - 50, 120, 50);
  }

  private drawHurtFlash(g: CanvasRenderingContext2D): void {
    const elapsed = this.tick - this.lastHurtTick;
    if (elapsed < 0 || elapsed > 6) return;
    const alpha = 0.35 * (1 - elapsed / 6);
    g.fillStyle = `rgba(220, 40, 40, ${alpha})`;
    g.fillRect(0, 0, WIDTH, HEIGHT);
  }

  private drawHud(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('HP', 8, HEIGHT - 18);
    g.fillStyle = this.hp <= 1 ? HUD_HP_LOW : HUD_HP_FULL;
    g.fillText('▌'.repeat(this.hp) + '·'.repeat(PLAYER_MAX_HP - this.hp), 30, HEIGHT - 18);

    g.textAlign = 'center';
    g.fillStyle = HUD_SCORE;
    g.fillText(`${this.score}`.padStart(6, '0'), WIDTH / 2, HEIGHT - 18);

    const remaining = this.enemies.reduce(
      (n, e) => n + (e.state === 'alive' ? 1 : 0),
      0
    );
    g.textAlign = 'right';
    g.fillStyle = HUD_TEXT;
    g.fillText(`TARGETS ${remaining}`, WIDTH - 8, HEIGHT - 18);

    g.textAlign = 'left';
  }

  private drawCrosshair(g: CanvasRenderingContext2D): void {
    g.fillStyle = HUD_TEXT;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    g.fillRect(cx - 3, cy, 6, 1);
    g.fillRect(cx, cy - 3, 1, 6);
  }

  private drawCard(
    g: CanvasRenderingContext2D,
    title: string,
    sub: string,
    cta: string
  ): void {
    const w = 280;
    const h = 100;
    const x = (WIDTH - w) / 2;
    const y = (HEIGHT - h) / 2;
    g.fillStyle = CARD_BG;
    g.fillRect(x, y, w, h);
    g.strokeStyle = CARD_BORDER;
    g.lineWidth = 1;
    g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = TITLE_TEXT;
    g.font = 'bold 18px ui-monospace, monospace';
    g.fillText(title, WIDTH / 2, y + 30);

    g.fillStyle = HUD_SCORE;
    g.font = '12px ui-monospace, monospace';
    g.fillText(sub, WIDTH / 2, y + 56);

    g.fillStyle = HUD_TEXT;
    g.font = '9px ui-monospace, monospace';
    // Blink CTA so the end card doesn't read as frozen.
    if ((this.tick - this.phaseEnteredTick) % 40 < 28) {
      g.fillText(cta, WIDTH / 2, y + 80);
    }

    g.textAlign = 'left';
    g.textBaseline = 'top';
  }

  private drawPerfHud(g: CanvasRenderingContext2D): void {
    let raySum = 0;
    let blitSum = 0;
    for (let i = 0; i < PERF_WINDOW; i++) {
      raySum += this.raycastSamples[i]!;
      blitSum += this.blitSamples[i]!;
    }
    const rayAvg = raySum / PERF_WINDOW;
    const blitAvg = blitSum / PERF_WINDOW;
    g.font = '8px ui-monospace, monospace';
    g.textAlign = 'right';
    g.textBaseline = 'top';
    g.fillStyle = HUD_TEXT;
    const x = WIDTH - 6;
    g.fillText(`RAY  ${rayAvg.toFixed(2)}ms`, x, 6);
    g.fillText(`BLIT ${blitAvg.toFixed(2)}ms`, x, 16);
    g.fillText(`TOT  ${(rayAvg + blitAvg).toFixed(2)}ms`, x, 26);
    g.fillText(`BUDGET 16.66`, x, 36);
    g.textAlign = 'left';
  }

  resize(_w: number, _h: number): void {}
  pause(): void {}
  resume(): void {}
  teardown(): void {}

  getScore(): number {
    return this.score;
  }

  getFinalScore(): number {
    return this.score;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
